import { z } from 'zod';
import { ToolReceipt } from './actions.ts';
import {
  ApprovalRequestClosedData,
  ApprovalRequestCreatedData,
  ApprovalRequestDecidedData,
} from './approval.ts';
import {
  CaseCreatedData,
  CaseHoldEventData,
  CaseLinkedData,
  CaseStateChangedData,
  CaseTimelineAppendedData,
} from './case.ts';
import { Id, IsoDateTime, JsonObject, newId, TenantId } from './common.ts';
import { DeadlineBreachedData } from './deadline.ts';
import { DocumentEventData, DocumentExpiringData } from './document.ts';
import { PlatformError } from './errors.ts';
import { IdentityExternalChangedData, IdentityLoginData, IdentityUserChangedData } from './identity.ts';
import { InboundEventNoticeData } from './inbound.ts';
import { RegistryModelManagementChangedData } from './model.ts';
import { NotificationEventData } from './notification.ts';
import { ObligationEventData } from './obligation.ts';
import { OperationStatusChangedData } from './operation.ts';
import { Principal, WorkloadRef } from './principal.ts';
import {
  ProcessActivityChangedData,
  ProcessChildCompletedData,
  ProcessChildStartedData,
  ProcessInstanceStartedData,
  ProcessInstanceStateChangedData,
} from './process.ts';
import { CaseContinuationStartedData, CaseResolutionChangedData } from './resolution.ts';
import {
  BundleEvalCompletedData,
  BundleEvalRequestedData,
  BundlePublishedData,
  BundleRetiredData,
  RegistryCatalogChangedData,
} from './studio.ts';
import { EpisodeOutcome, TaskState, TimelineEntry } from './task.ts';
import { WorkItemOverdueData } from './work-item.ts';

/** CloudEvents 1.0 envelope plus platform routing fields. `type` is a dotted event name. */
export const PlatformEvent = z
  .object({
    specversion: z.literal('1.0'),
    id: z.string().min(1),
    /** Producer, e.g. `agentic://core/tasks`. */
    source: z.string().min(1),
    type: z.string().min(1),
    /** Entity key the event is about (usually the task id). */
    subject: z.string().optional(),
    time: IsoDateTime,
    datacontenttype: z.literal('application/json').default('application/json'),
    dataschema: z.string().optional(),
    data: z.unknown(),
    tenantId: TenantId,
    /** Correlates every event of one task/request (usually the task id or trace id). */
    correlationId: z.string().min(1),
    /** Id of the event that caused this one. */
    causationId: z.string().optional(),
  })
  .meta({ id: 'PlatformEvent' });
export type PlatformEvent<T = unknown> = Omit<z.infer<typeof PlatformEvent>, 'data'> & { data: T };

/** Known event types. Modules may publish additional `<module>.<entity>.<verb>` types. */
export const EVENT_TYPES = {
  taskCreated: 'task.created',
  taskStateChanged: 'task.state.changed',
  taskTimelineAppended: 'task.timeline.appended',
  workflowRunStarted: 'workflow.run.started',
  workflowStepStarted: 'workflow.step.started',
  workflowStepFinished: 'workflow.step.finished',
  workflowRunSuspended: 'workflow.run.suspended',
  workflowRunResumed: 'workflow.run.resumed',
  workflowRunFinished: 'workflow.run.finished',
  /** Aggregated step event forwarded to SSE clients. */
  workflowStep: 'workflow.step',
  episodeTrace: 'episode.trace',
  approvalRequested: 'approval.requested',
  approvalDecided: 'approval.decided',
  actionReceipt: 'action.receipt',
  auditEntry: 'audit.entry',
  registryKillSwitchChanged: 'registry.killswitch.changed',
  modelFallback: 'model.fallback',
  /** Added by 09: the command "start this task", published in the task's create transaction by intake. */
  taskAccepted: 'task.accepted',
  /** Added by 09: clarification answers for a task's run, published with the timeline entry by intake. */
  taskClarificationAnswered: 'task.clarification.answered',
  /** Added by 11a: evaluate a bundle (authoring module → `registry.bundle-evals`). */
  agentBundleEvalRequested: 'agent.bundle.eval.requested',
  /** Added by 11a: the eval gate result (apps/platform runner → the requester). */
  agentBundleEvalCompleted: 'agent.bundle.eval.completed',
  /** Added by 11a: admit a bundle (authoring module or `agentic bundle import` → `registry.bundles`). */
  agentBundlePublished: 'agent.bundle.published',
  /** Added by 11a: retire a bundle agent (authoring module or `agentic bundle retire` → `registry.bundles`). */
  agentBundleRetired: 'agent.bundle.retired',
  /** Added by 11a: an admission decision or retirement (registry → live tail in every process). */
  registryCatalogChanged: 'registry.catalog.changed',
  /** Added by 23: a model-management mutation committed (cache invalidation only). */
  registryModelManagementChanged: 'registry.model-management.changed',
} as const;
export type KnownEventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

/** Event types streamed to clients on `GET /tasks/{taskId}/events`. SSE `event:` = type, `data:` = PlatformEvent JSON. */
export const SSE_EVENT_TYPES = [
  'task.state.changed',
  'workflow.step',
  'episode.trace',
  'approval.requested',
  'approval.decided',
  'action.receipt',
  'audit.entry',
  /** Added by 02e: live timeline entries; also used to replay the timeline when a stream connects. */
  'task.timeline.appended',
] as const;
export const SseEventType = z.enum(SSE_EVENT_TYPES).meta({ id: 'SseEventType' });

/**
 * Added by 20a: event types streamed on `GET /cases/{caseId}/events` (SSE `event:` = type, `data:` = PlatformEvent
 * JSON, `id:` = event id): the case's own projection events, its activities' changes and the state changes of the
 * tasks behind its agent activities. On connect the stream replays the case timeline as `case.timeline.appended`.
 */
export const CASE_SSE_EVENT_TYPES = [
  'case.timeline.appended',
  'case.state.changed',
  'process.activity.changed',
  'task.state.changed',
  // Added by 26a (brief 26 §3.4).
  'obligation.opened',
  'obligation.met',
  'obligation.waived',
  'obligation.overdue',
  'obligation.escalated',
  'obligation.undeliverable',
  'case.resolution.changed',
  'case.continuation.started',
] as const;
export const CaseSseEventType = z.enum(CASE_SSE_EVENT_TYPES).meta({ id: 'CaseSseEventType' });
export type CaseSseEventType = z.infer<typeof CaseSseEventType>;
export type SseEventType = z.infer<typeof SseEventType>;

export const TaskCreatedData = z.object({ taskId: Id, state: TaskState, capability: z.string() });
export type TaskCreatedData = z.infer<typeof TaskCreatedData>;

export const TaskStateChangedData = z
  .object({
    taskId: Id,
    from: TaskState,
    to: TaskState,
    reason: z.string(),
    by: z.string(),
    at: IsoDateTime,
    /** Added by 20a: the task summary written with a terminal transition. */
    summary: z.string().optional(),
    /** Added by 20a: the episode outcome written with a terminal transition. */
    episodeOutcome: EpisodeOutcome.optional(),
  })
  .meta({ id: 'TaskStateChangedData' });
export type TaskStateChangedData = z.infer<typeof TaskStateChangedData>;

export const TaskTimelineAppendedData = TimelineEntry;
export type TaskTimelineAppendedData = z.infer<typeof TaskTimelineAppendedData>;

export const WorkflowStepData = z
  .object({
    runId: Id,
    taskId: Id,
    step: z.string(),
    status: z.enum(['started', 'succeeded', 'failed', 'suspended', 'resumed']),
    attempt: z.number().int().nonnegative(),
    at: IsoDateTime,
    error: z.object({ code: z.string(), message: z.string() }).optional(),
  })
  .meta({ id: 'WorkflowStepData' });
export type WorkflowStepData = z.infer<typeof WorkflowStepData>;

export const EpisodeTraceData = z
  .object({
    taskId: Id,
    stepId: z.string(),
    node: z.string(),
    kind: z.enum(['node', 'model', 'tool', 'retrieve']),
    summary: z.string(),
    durationMs: z.number().nonnegative(),
    usage: z.object({ inputTokens: z.number().int(), outputTokens: z.number().int() }).optional(),
    redacted: z.boolean(),
    at: IsoDateTime,
  })
  .meta({ id: 'EpisodeTraceData' });
export type EpisodeTraceData = z.infer<typeof EpisodeTraceData>;

export const ApprovalRequestedData = z
  .object({ approvalId: Id, taskId: Id, actionIds: z.array(Id), expiresAt: IsoDateTime })
  .meta({ id: 'ApprovalRequestedData' });
export type ApprovalRequestedData = z.infer<typeof ApprovalRequestedData>;

export const ApprovalDecidedData = z
  .object({
    approvalId: Id,
    taskId: Id,
    decision: z.enum(['approved', 'rejected']),
    approverId: z.string(),
    decidedAt: IsoDateTime,
    rationale: z.string().optional(),
  })
  .meta({ id: 'ApprovalDecidedData' });
export type ApprovalDecidedData = z.infer<typeof ApprovalDecidedData>;

export const ActionReceiptData = ToolReceipt;
export type ActionReceiptData = z.infer<typeof ActionReceiptData>;

export const AuditEntryData = z
  .object({
    kind: z.string(),
    actor: z.string(),
    taskId: Id.optional(),
    actionId: Id.optional(),
    payload: z.record(z.string(), z.unknown()),
    at: IsoDateTime,
  })
  .meta({ id: 'AuditEntryData' });
export type AuditEntryData = z.infer<typeof AuditEntryData>;

export const KillSwitchChangedData = z
  .object({
    kind: z.enum(['agent', 'tool', 'workload']),
    name: z.string(),
    enabled: z.boolean(),
    by: z.string(),
    reason: z.string(),
    at: IsoDateTime,
  })
  .meta({ id: 'KillSwitchChangedData' });
export type KillSwitchChangedData = z.infer<typeof KillSwitchChangedData>;

/** Payload schema per SSE event type. */
export const SSE_EVENT_DATA = {
  'task.state.changed': TaskStateChangedData,
  'workflow.step': WorkflowStepData,
  'episode.trace': EpisodeTraceData,
  'approval.requested': ApprovalRequestedData,
  'approval.decided': ApprovalDecidedData,
  'action.receipt': ActionReceiptData,
  'audit.entry': AuditEntryData,
  'task.timeline.appended': TaskTimelineAppendedData,
} as const satisfies Record<SseEventType, z.ZodType>;

/**
 * `task.accepted` (added by 09): the start command for a task. Carries references only (no bearer credential):
 * the starter consumer re-mints a fresh `ExecutionContext` from these fields. `actor` is the requester's
 * identity claims as resolved at intake (roles, ACL subjects), so the run acts as the same principal.
 */
export const TaskAcceptedData = z
  .object({
    taskId: Id,
    capability: z.string().min(1),
    capabilityVersion: z.string().min(1),
    tenantId: TenantId,
    requestedBy: z.string().min(1),
    domain: z.string().min(1).optional(),
    delegationRef: z.string().optional(),
    policyVersion: z.string().optional(),
    workload: WorkloadRef.optional(),
    idempotencyKey: z.string().min(1),
    actor: Principal.optional(),
  })
  .meta({ id: 'TaskAcceptedData' });
export type TaskAcceptedData = z.infer<typeof TaskAcceptedData>;

/** `task.clarification.answered` (added by 09): answers for the run waiting on signal `clarification`. */
export const TaskClarificationAnsweredData = z
  .object({
    taskId: Id,
    runId: Id.optional(),
    answers: JsonObject,
    by: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'TaskClarificationAnsweredData' });
export type TaskClarificationAnsweredData = z.infer<typeof TaskClarificationAnsweredData>;

/** `workflow.run.*` lifecycle events (engines may add fields such as `reason`, `error`, `previous`). */
export const WorkflowRunEventData = z
  .looseObject({
    runId: Id,
    taskId: Id,
    definition: z.string(),
    version: z.string(),
    state: z.string(),
    attempt: z.number().int().nonnegative(),
    at: IsoDateTime,
  })
  .meta({ id: 'WorkflowRunEventData' });
export type WorkflowRunEventData = z.infer<typeof WorkflowRunEventData>;

/** `model.fallback`: the model router switched profiles after a provider error. */
export const ModelFallbackEventData = z
  .looseObject({ from: z.string(), to: z.string(), reason: z.string() })
  .meta({ id: 'ModelFallbackEventData' });

/** `knowledge.source.*`: a knowledge source changed (ids and versions only, never content). */
export const KnowledgeSourceEventData = z
  .looseObject({
    sourceId: z.string().min(1),
    scope: z.unknown().optional(),
    version: z.unknown().optional(),
  })
  .meta({ id: 'KnowledgeSourceEventData' });

/** `knowledge.index.rebuilt` (added by 10): a source's index entries were rebuilt (re-embedded or re-indexed). */
export const KnowledgeIndexRebuiltData = z
  .looseObject({ index: z.string().min(1), sources: z.number().int().nonnegative(), at: IsoDateTime })
  .meta({ id: 'KnowledgeIndexRebuiltData' });

/** `knowledge.memory.validated`. */
export const KnowledgeMemoryValidatedData = z
  .looseObject({ memoryId: z.string().min(1), scope: z.unknown().optional(), by: z.string() })
  .meta({ id: 'KnowledgeMemoryValidatedData' });

/** Version stamped into `dataschema` (`agentic://events/<type>@<version>`) for schemas registered here. */
export const EVENT_SCHEMA_VERSION = 1;

/**
 * Data schema per event type (added by 09). `createEvent` validates `data` against it and stamps
 * `dataschema = agentic://events/<type>@1`. Packs add `<module>.<entity>.<verb>` schemas through
 * `registerEventSchema` (re-exported by the sdk). Types without a schema pass with a warning.
 */
export const EVENT_SCHEMAS: Record<string, z.ZodType> = {
  'task.created': TaskCreatedData,
  'task.state.changed': TaskStateChangedData,
  'task.timeline.appended': TaskTimelineAppendedData,
  'task.accepted': TaskAcceptedData,
  'task.clarification.answered': TaskClarificationAnsweredData,
  'workflow.run.started': WorkflowRunEventData,
  'workflow.run.suspended': WorkflowRunEventData,
  'workflow.run.resumed': WorkflowRunEventData,
  'workflow.run.finished': WorkflowRunEventData,
  'workflow.step.started': WorkflowStepData,
  'workflow.step.finished': WorkflowStepData,
  'workflow.step': WorkflowStepData,
  'episode.trace': EpisodeTraceData,
  'approval.requested': ApprovalRequestedData,
  'approval.decided': ApprovalDecidedData,
  'action.receipt': ActionReceiptData,
  'audit.entry': AuditEntryData,
  'registry.killswitch.changed': KillSwitchChangedData,
  'model.fallback': ModelFallbackEventData,
  'knowledge.source.ingested': KnowledgeSourceEventData,
  'knowledge.source.deleted': KnowledgeSourceEventData,
  // 10: published by the freshness sweep and by an index rebuild.
  'knowledge.source.stale': KnowledgeSourceEventData,
  'knowledge.index.rebuilt': KnowledgeIndexRebuiltData,
  'knowledge.memory.validated': KnowledgeMemoryValidatedData,
  // 08 identity (registered at integration).
  'identity.login': IdentityLoginData,
  'identity.user.created': IdentityUserChangedData,
  'identity.user.roles.changed': IdentityUserChangedData,
  'identity.user.disabled': IdentityUserChangedData,
  'identity.user.enabled': IdentityUserChangedData,
  // 21b external principals (core-identity-admin).
  'identity.external.invited': IdentityExternalChangedData,
  'identity.external.revoked': IdentityExternalChangedData,
  // 11a registry bundles (built in: the registry and apps/platform publish them).
  'agent.bundle.eval.requested': BundleEvalRequestedData,
  'agent.bundle.eval.completed': BundleEvalCompletedData,
  'agent.bundle.published': BundlePublishedData,
  'agent.bundle.retired': BundleRetiredData,
  'registry.catalog.changed': RegistryCatalogChangedData,
  // 23 model management (registry; cache invalidation only).
  'registry.model-management.changed': RegistryModelManagementChangedData,
  // 17a process composition and governance (built in; producers arrive with 17b).
  'process.instance.started': ProcessInstanceStartedData,
  'process.instance.state.changed': ProcessInstanceStateChangedData,
  'process.activity.changed': ProcessActivityChangedData,
  'case.created': CaseCreatedData,
  'case.state.changed': CaseStateChangedData,
  'case.linked': CaseLinkedData,
  'case.timeline.appended': CaseTimelineAppendedData,
  'approval.request.created': ApprovalRequestCreatedData,
  'approval.request.decided': ApprovalRequestDecidedData,
  'approval.request.closed': ApprovalRequestClosedData,
  'operation.status.changed': OperationStatusChangedData,
  'inbound.event.received': InboundEventNoticeData,
  'inbound.event.disposed': InboundEventNoticeData,
  // 21 step 0 (brief 21 §2, ADR-021): generic process primitives (producers arrive with 21a–21c).
  'work-item.overdue': WorkItemOverdueData,
  'deadline.breached': DeadlineBreachedData,
  'case.held': CaseHoldEventData,
  'case.resumed': CaseHoldEventData,
  'document.uploaded': DocumentEventData,
  'document.verified': DocumentEventData,
  // document-delete: retired with the bytes removed (`DELETE /documents/{documentId}`).
  'document.deleted': DocumentEventData,
  'document.expiring': DocumentExpiringData,
  'obligation.opened': ObligationEventData,
  'obligation.met': ObligationEventData,
  'obligation.waived': ObligationEventData,
  // 26a (brief 26 §3.4): obligation flags and revisions, case outcome and continuation runs.
  'obligation.overdue': ObligationEventData,
  'obligation.escalated': ObligationEventData,
  'obligation.undeliverable': ObligationEventData,
  'obligation.answered': ObligationEventData,
  'case.resolution.changed': CaseResolutionChangedData,
  'case.continuation.started': CaseContinuationStartedData,
  'notification.sent': NotificationEventData,
  'notification.failed': NotificationEventData,
  'process.child.started': ProcessChildStartedData,
  'process.child.completed': ProcessChildCompletedData,
};

const BUILT_IN_EVENT_TYPES = new Set(Object.keys(EVENT_SCHEMAS));
const EVENT_TYPE_NAME = /^[a-z][a-z0-9-]*(\.[a-z0-9][a-z0-9_-]*)+$/;

/**
 * Registers the data schema of an event type (packs: `<module>.<entity>.<verb>`). Re-registering the same
 * schema object is a no-op; replacing a built-in platform type's schema is refused.
 */
export function registerEventSchema(type: string, schema: z.ZodType): void {
  if (!EVENT_TYPE_NAME.test(type)) {
    throw new PlatformError('validation_failed', `Event type "${type}" must be dotted lowercase`, {
      details: { type },
    });
  }
  if (EVENT_SCHEMAS[type] === schema) return;
  if (BUILT_IN_EVENT_TYPES.has(type)) {
    throw new PlatformError('conflict', `Event type "${type}" is a platform type; its schema is fixed`, {
      details: { type },
    });
  }
  EVENT_SCHEMAS[type] = schema;
}

/** The `dataschema` URI of a type with a registered schema. */
export function eventDataSchemaUri(type: string): string {
  return `agentic://events/${type}@${EVENT_SCHEMA_VERSION}`;
}

let unknownTypeWarning: ((type: string) => void) | undefined;
const warnedTypes = new Set<string>();

/**
 * Sets the callback `createEvent` calls once per event type that has no registered schema (the kernel points
 * it at its logger). Contracts stay dependency-free, so the default is silent.
 */
export function onUnknownEventType(callback: ((type: string) => void) | undefined): void {
  unknownTypeWarning = callback;
}

export interface CreateEventInput<T> {
  type: string;
  source: string;
  data: T;
  tenantId: string;
  correlationId: string;
  subject?: string;
  causationId?: string;
  dataschema?: string;
  id?: string;
  time?: string;
}

/**
 * Builds a PlatformEvent with a fresh UUID v7 id and the current time. When `EVENT_SCHEMAS` has a schema for
 * the type, `data` is validated (`PlatformError('validation_failed')` on mismatch; the data is kept as given)
 * and `dataschema` defaults to `agentic://events/<type>@1`.
 */
export function createEvent<T>(input: CreateEventInput<T>): PlatformEvent<T> {
  const schema = EVENT_SCHEMAS[input.type];
  let dataschema = input.dataschema;
  if (schema) {
    const parsed = schema.safeParse(input.data);
    if (!parsed.success) {
      throw new PlatformError('validation_failed', `Invalid data for event ${input.type}`, {
        details: { type: input.type, reason: 'event_data_invalid', issues: parsed.error.issues },
      });
    }
    dataschema ??= eventDataSchemaUri(input.type);
  } else if (!warnedTypes.has(input.type)) {
    warnedTypes.add(input.type);
    unknownTypeWarning?.(input.type);
  }
  return {
    specversion: '1.0',
    id: input.id ?? newId(),
    source: input.source,
    type: input.type,
    time: input.time ?? new Date().toISOString(),
    datacontenttype: 'application/json',
    data: input.data,
    tenantId: input.tenantId,
    correlationId: input.correlationId,
    ...(input.subject === undefined ? {} : { subject: input.subject }),
    ...(input.causationId === undefined ? {} : { causationId: input.causationId }),
    ...(dataschema === undefined ? {} : { dataschema }),
  };
}

/** True when `type` matches `pattern` (exact, `*`, or trailing `*` prefix like `task.*`). */
export function eventTypeMatches(pattern: string, type: string): boolean {
  if (pattern === '*') return true;
  if (pattern.endsWith('*')) return type.startsWith(pattern.slice(0, -1));
  return pattern === type;
}
